
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ChangeDetectionStrategy, inject, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  MealKitCircularProgressBarComponent,
  MealKitEstablishmentComponent,
  SubEstablishment,
} from '@yoni/mealcomponents-lib';
import { CookieHistoryImpl } from 'app/RestaurantHistoryManager/CookieHistoryImpl';
import { RestaurantHistoryManager } from 'app/RestaurantHistoryManager/RestaurantHistoryManager';
import {
  Customer,
  PerfoIndicator,
  QdmService,
  Restaurant,
} from '../../Services/qdm.service';
import { Subscription } from 'rxjs';
import { ModalService } from '../../Services/modal.service';
import { ModalShowPerformances } from '../Modal/ModalShowPerformances/ModalShowPerformances';
import {AuthService, User} from '../../Services/auth.service';
import {
  Company,
  EnterpriseResponse,
  GestionService,
} from '../../Services/gestion.service';
import { ModalRestaurantInfosComponent } from '../Modal/ModalRestaurantInfos/ModalRestaurantInfos.component';
import { EnvLoader } from 'app/Environment/EnvLoader';

@Component({
    imports: [
    TranslateModule,
    MealKitEstablishmentComponent,
    MealKitCircularProgressBarComponent
],
    selector: 'informations-band',
    templateUrl: 'InformationsBandModule.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['InformationsBandModule.component.scss']
})
export class InformationsBandModuleComponent
  implements AfterViewInit, OnChanges, OnDestroy, OnInit
{
  private qdmService = inject(QdmService);
  private restHistory = inject(RestaurantHistoryManager);
  private renderer = inject(Renderer2);
  private auth = inject(AuthService);
  private gestion = inject(GestionService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private modal = inject(ModalService);

  protected selectedRestaurant: Restaurant | undefined = undefined;
  protected restaurants: Restaurant[] = [];
  protected restaurantHistory: Restaurant[] = [];
  protected enterpriseData: EnterpriseResponse | null = null;

  // Tips du jour, avec un tips par défaut si aucun n'est trouvé
  protected tipOfTheDay: string = '';
  protected tipOfTheDayAuthor: string = '';
  protected tipType: string = '';

  // Cooldown pour le changement de restaurant
  protected cooldownActive: boolean = false;

  readonly selector = viewChild('selector', { read: ElementRef });

  protected subscription: Subscription = new Subscription();
  protected perfos: PerfoIndicator[] = [];
  protected monthName: string = '';
  private customer: Customer | null = null;
  protected themeJson: any = {};
  protected sub: SubEstablishment | undefined;
  companyData: Company | undefined = undefined;
  user?: User;
  boGestionUrl: string = '';


  ngOnInit(): void {
    this.boGestionUrl = EnvLoader.load().boGestionUrl;

    this.subscription.add(this.auth.user$.subscribe(user => {
      this.user = user;
      this.loadCgv();
    }));
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.qdmService.loadRestaurantsHistory();
  }

  ngAfterViewInit(): void {
    this.qdmService.loadRestaurantsHistory();

    this.subscription.add(
      this.qdmService.selectedEnterprise$.subscribe((ent) => {
        if (!ent) {
          this.sub = undefined;
          return;
        }

        this.sub = { id: ent.id, name: ent.name };
        this.cdr.detectChanges();
      })
    );

    this.subscription.add(
      this.qdmService.selectedRestaurant$.subscribe((restaurant) => {
        this.selectedRestaurant = restaurant;
        this.perfos = [];
        this.cdr.detectChanges();
        if (restaurant) {
          this.loadCgv();
        } else {
          this.companyData = undefined;
        }

        if (restaurant) {
          this.qdmService
            .fetchRestaurantById(restaurant?.id)
            .then((restaurant) => {
              this.perfos = restaurant.perfo;
              if (this.perfos.length > 0) {
                this.monthName = new Date(this.perfos[0].month).formatDate(
                  'month'
                );
              }
              this.cdr.detectChanges();
            });
        } else {
          this.perfos = [];
          this.cdr.detectChanges();
        }
      })
    );

    this.subscription.add(
      this.qdmService.restaurants$.subscribe((restaurants) => {
        this.restaurants = restaurants;
        this.cdr.detectChanges();
      })
    );

    this.subscription.add(
      this.qdmService.restaurantsHistory$.subscribe((restaurantsHistory) => {
        const ent = this.auth.userEnterpriseSubject.value;
        if (!ent) {
          this.restaurantHistory = restaurantsHistory;
          return;
        }

        const arr = restaurantsHistory;

        arr.forEach((rest) => {
          rest.sub = [];
          const enabledEnterprises = ent.enterprises.filter(e => e.isEnabled);
          rest.sub = enabledEnterprises.map((e) => ({ id: e.id, name: e.name, companyId: e.companies[0].reference})).filter((e) => parseInt(e.companyId) === rest.id);
          if(rest.sub.length === 1)rest.sub = [];
        });

        this.restaurantHistory = arr;
        this.cdr.detectChanges();
      })
    );

    this.subscription.add(
      this.qdmService.currentCustomer$.subscribe((customer) => {
        if(!customer)return;

        this.customer = customer;
        this.themeJson = JSON.parse(this.customer?.themeJson || '{}');
        this.cdr.detectChanges();

        let customerId: number | undefined = !customer
          ? undefined
          : customer.customer_id;

        this.qdmService
          .getTipOfTheDay(customerId)
          .then((tips) => {
            if (tips.length > 0) {
              this.tipOfTheDay = tips[0].texte;
              this.tipOfTheDayAuthor = tips[0].auteur;
              this.tipType = tips[0].type;
              this.cdr.detectChanges();
            }else{
              this.tipOfTheDay = "";
              this.tipOfTheDayAuthor = "";
              this.tipType = "";
              this.cdr.detectChanges();
            }
          })
          .catch(() => {
            alert('Error while fetching tip of the day');
          });
      })
    );

    this.subscription.add(
      this.auth.userEnterprise$.subscribe((ent) => {
        if (!ent) {
          this.sub = undefined;
          return;
        }

        const arr = this.restaurantHistory;

        arr.forEach((rest) => {
          rest.sub = [];
          const enabledEnterprises = ent.enterprises.filter(e => e.isEnabled);
          rest.sub = enabledEnterprises.map((e) => ({ id: e.id, name: e.name, companyId: e.companies[0].reference})).filter((e) => parseInt(e.companyId) === rest.id);
          if(rest.sub.length === 1)rest.sub = [];
        });


        this.restaurantHistory = [];
        this.cdr.detectChanges();

        this.restaurantHistory = arr;
        this.cdr.detectChanges();
      })
    );
  }

  selectedRestaurantChanged($restaurant: Restaurant | undefined) {
    // Si le cooldown est actif, ignorer la demande de changement
    if (this.cooldownActive) {
      return;
    }

    this.modal.close();
    if (!$restaurant) {
      this.qdmService.selectRestaurant(undefined);
      return;
    }

    if (
      $restaurant.id === this.qdmService.selectedRestaurantSubject.value?.id
    ) {
      return;
    }

    // Activer le cooldown
    this.cooldownActive = true;
    setTimeout(() => {
      this.cooldownActive = false;
      this.cdr.detectChanges();
    }, 1000);

    // Réinitialiser l'enfant sélectionné lors du changement de restaurant
    this.qdmService.selectedChildSubject.next(null);

    // Sélectionner le nouveau restaurant (la méthode selectRestaurant se charge
    // de réinitialiser toutes les données liées au restaurant précédent)
    this.qdmService.selectRestaurant($restaurant);

    if ($restaurant.is_use_password) {
      this.router.navigate(['/']).then(() => {
        this.qdmService.selectRestaurant($restaurant);
        // Ajouter un délai avant la navigation pour laisser le temps aux données d'être chargées
        setTimeout(() => {
          this.router.navigate(['/restaurant', $restaurant.id]);
        }, 500);
      });
    } else {
      // Ajouter un délai avant la navigation pour laisser le temps aux données d'être chargées
      setTimeout(() => {
        this.router.navigate(['/restaurant', $restaurant.id]);
      }, 500);
    }
  }

  establishmentRemoveRequest(res: Restaurant) {
    const id = res.id;
    if (this.selectedRestaurant) {
      if (this.selectedRestaurant.id === id) {
        this.router.navigate(['/']);
      }
    }
    this.restaurantHistory = this.restHistory.removeRestaurantById(
      new CookieHistoryImpl(),
      id
    );
  }

  displayPerfosModal(perfos: PerfoIndicator[]) {
    this.modal.open(
      ModalShowPerformances,
      { perfos: perfos, monthName: this.monthName },
      () => {}
    );
  }

  showRestaurantInformations() {
    if (!this.selectedRestaurant) return;

    this.modal.open(
      ModalRestaurantInfosComponent,
      { restaurant: this.selectedRestaurant, closeText: 'Fermer' },
      () => {}
    );
  }

  subEstablishmentSelected($event: { establishment: any; sub: any }) {
    const userEnt = this.auth.userEnterpriseSubject.value;
    if (!userEnt) return;

    const ent = userEnt.enterprises.find((e) => e.id === $event.sub.id);

    if (ent) {
      this.qdmService.selectEnterprise(ent);
      this.cdr.detectChanges();
    }
  }

  /**
   * Cette méthode n'agit que si on est connecté ET si un restaurant est sélectionné
   * ET que le restaurant utilise is_use_password
   * @private
   */
  private loadCgv() {
    if(!this.selectedRestaurant)return;
    if(!this.user)return;
    if(!this.selectedRestaurant.is_use_password)return;

    this.gestion.fetchCompany(this.selectedRestaurant.id, this.selectedRestaurant.cstID).then((company) => {
      this.companyData = company;
      this.cdr.detectChanges();
    });
  }
}
